import { useState, useMemo, useCallback } from 'react';

/**
 * Custom hook for table pagination
 * Handles pagination logic and page calculation
 */
export function usePagination(data, initialItemsPerPage = 10) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Calculate pagination values
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = useMemo(
    () => data.slice(startIndex, endIndex),
    [data, startIndex, endIndex]
  );

  // Generate pagination page numbers with stable width
  const getPageNumbers = (current, total) => {
    const pages = [];
    
    // If 7 or fewer pages, show all
    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Always show first page
    pages.push(1);

    // Calculate the middle range (always show 5 buttons: current ± 2)
    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(total - 1, current + 2);

    // Adjust to always show 5 middle buttons when possible
    if (current <= 4) {
      startPage = 2;
      endPage = 5;
    } else if (current >= total - 3) {
      startPage = total - 4;
      endPage = total - 1;
    }

    // Left ellipsis (only if gap > 1)
    if (startPage > 2) {
      pages.push("...");
    }

    // Middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Right ellipsis (only if gap > 1)
    if (endPage < total - 1) {
      pages.push("...");
    }

    // Always show last page
    pages.push(total);

    return pages;
  };

  // Reset to page 1 when data or itemsPerPage changes
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(Number(newItemsPerPage));
    setCurrentPage(1);
  };

  // Go to specific page
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Navigation helpers
  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    itemsPerPage,
    totalPages,
    paginatedData,
    pageNumbers: getPageNumbers(currentPage, totalPages),
    goToPage,
    goToNextPage,
    goToPreviousPage,
    resetToFirstPage,
    setItemsPerPage: handleItemsPerPageChange,
  };
}
